import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AxiosError } from 'axios';
import { useToast } from '@centraoneplus/ui';
import { useUrlToken } from '@hooks/useUrlToken';
import { useAuthStore } from '@stores/authStore';
import {
  platformAuthService,
  type PlatformRegistrationCheck,
} from '@services/platform-auth.service';
import { PasswordInput } from '@components/PasswordInput';
import { AuthLinkProblem } from '@components/auth/AuthLinkProblem';
import { CredentialEncryptionError } from '@/lib/credentialEncryption';
import { newPasswordFormSchema, PASSWORD_REQUIREMENTS_MESSAGE } from '@/lib/validation';
import { platformRegistrationError } from './PlatformRegisterPage';

type PageState = 'loading' | 'valid' | 'invalid' | 'error';

/**
 * Platform-admin registration, step 2: the page the emailed verification link
 * opens. Collects name + password, creates the account and signs in.
 */
export default function PlatformRegisterCompletePage() {
  const token = useUrlToken();
  const [pageState, setPageState] = useState<PageState>('loading');
  const [check, setCheck] = useState<PlatformRegistrationCheck | null>(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const platformLogin = useAuthStore((state) => state.platformLogin);
  const toast = useToast();

  useEffect(() => {
    if (!token) {
      setPageState('invalid');
      setError('This verification link is missing its code. Open the link from your email again.');
      return;
    }
    let cancelled = false;
    platformAuthService
      .verifyRegistration(token)
      .then((data) => {
        if (cancelled) return;
        setCheck(data);
        setPageState('valid');
      })
      .catch((err: AxiosError) => {
        if (cancelled) return;
        const status = err.response?.status;
        setPageState(status && status < 500 && status !== 429 ? 'invalid' : 'error');
        setError(platformRegistrationError(err));
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!firstName.trim() || !lastName.trim()) {
      setError('Please enter your first and last name.');
      return;
    }
    const result = newPasswordFormSchema.safeParse({ password, confirmPassword });
    if (!result.success) {
      setError(result.error.issues[0]?.message ?? PASSWORD_REQUIREMENTS_MESSAGE);
      return;
    }

    setLoading(true);
    try {
      const { user, accessToken } = await platformAuthService.completeRegistration({
        verificationToken: token,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        password,
      });
      platformLogin(
        {
          id: user.id,
          email: user.email,
          name: user.displayName || user.firstName || user.email,
          role: user.platformRole,
          tenantId: '',
          isPlatformUser: true,
          platformRole: user.platformRole,
          isPrimary: !!user.isPrimary,
        },
        accessToken,
      );
      toast.success('Account created', `Welcome, ${user.displayName || user.email}!`);
      navigate('/platform/tenants', { replace: true });
    } catch (err) {
      setError(err instanceof CredentialEncryptionError ? err.message : platformRegistrationError(err));
    } finally {
      setLoading(false);
    }
  };

  if (pageState === 'loading') {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Checking your verification link...</p>
        </div>
      </div>
    );
  }

  if (pageState !== 'valid') {
    return (
      <AuthLinkProblem
        title={
          pageState === 'error'
            ? 'This link could not be checked'
            : "This verification link doesn't work"
        }
        message={error}
      />
    );
  }

  return (
    <div>
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Email verified</h2>
        <p className="mt-2 text-gray-600 dark:text-gray-400">
          {check?.registrationType === 'setup'
            ? 'Set up the first Main Super Admin account for the platform.'
            : 'Finish creating your Platform Admin account.'}
        </p>
      </div>

      <form className="space-y-6" onSubmit={handleSubmit}>
        {error && (
          <div className="px-4 py-3 rounded bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300">
            {error}
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Email address</label>
          <input
            type="email"
            value={check?.email || ''}
            disabled
            className="input mt-1 bg-gray-50 dark:bg-gray-700 cursor-not-allowed dark:text-gray-300"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Your role</label>
          <div className="mt-1 px-3 py-2 bg-gray-50 dark:bg-gray-700 rounded-md border border-gray-300 dark:border-gray-600">
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 dark:bg-primary-900 text-primary-800 dark:text-primary-200">
              {check?.registrationType === 'setup' ? 'Main Super Admin' : 'Platform Admin'}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              First name
            </label>
            <input
              id="firstName"
              name="firstName"
              type="text"
              autoComplete="given-name"
              required
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="input mt-1 dark:bg-gray-800 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
              placeholder="John"
            />
          </div>
          <div>
            <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Last name
            </label>
            <input
              id="lastName"
              name="lastName"
              type="text"
              autoComplete="family-name"
              required
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className="input mt-1 dark:bg-gray-800 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
              placeholder="Doe"
            />
          </div>
        </div>

        <div>
          <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Create password
          </label>
          <PasswordInput
            id="password"
            name="password"
            autoComplete="new-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="input dark:bg-gray-800 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
            wrapperClassName="mt-1"
            placeholder="Enter a strong password"
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            At least 12 characters with uppercase, lowercase, number, and special character
          </p>
        </div>

        <div>
          <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Confirm password
          </label>
          <PasswordInput
            id="confirmPassword"
            name="confirmPassword"
            autoComplete="new-password"
            required
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="input dark:bg-gray-800 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
            wrapperClassName="mt-1"
            placeholder="Confirm your password"
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Creating your account...' : 'Create account'}
        </button>
      </form>
    </div>
  );
}
